/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import { createEditor } from 'slate'
import { COLORS } from '@btime/constants'
import { compose, find, identity, is, prop, propEq } from 'ramda'

import { Absolute, Relative } from '../../../kiti/components/helpers/Position'
import { useControlledDropdown } from '../../../kiti/utils/helpers/controlledDropdown'
import { useLazyOptions } from '../../../kiti/utils/helpers/options'
import { useOnStopTypingState } from '../../../kiti/utils/helpers/onStopTyping'
import Input from '../../../kiti/components/form/Input'
import LabelIconListItem from '../../../kiti/components/list/LabelIconListItem'
import Loading from '../../../kiti/components/helpers/Loading'
import Panel from '../../../kiti/components/structure/Panel'
import Picker from '../../../kiti/components/form/picker/Picker'
import Separator from '../../../kiti/components/helpers/Separator'
import Text from '../../../kiti/components/presentation/Text'
import InterpolationPlugin from '../_plugins/InterpolationPlugin'
import { Editable, Slate, withReact } from 'slate-react'
import { deserializeText, serializeText } from '../_serializers/textSerializer'
import { useEditorKeydown } from '../_plugins/keydowns'
import { renderElement, renderLeaf } from '../_plugins/plugins'
import EditorContext from '../EditorContext'
import { INTERPOLATION_REGEX, isInterpolation } from '../../../../utils/helpers/interpolation'
import UniqueInterpolationPlugin from '../_plugins/UniqueInterpolationPlugin'

const SelectWithInterpolation = ({
  t,
  value,
  disabled,
  onChange,
  onCreate,
  header,
  idField,
  labelField,
  descriptionField,
  options,
  selectedLabel,
  multiple,
  selectOnlyId,
  disableEmptyOption,
  onClose,
  disablePagination,
  disableItem,
  reasonForDisablePresenter,
  interpolations = [],
  placeholder,
  onKeyDown,
  assign,
  origin,
  ...props
}) => {
  const styles = css`
    & .panel {
      border-color: ${COLORS.light5};
    }
    & .inputButtonWrapper .input {
      min-height: 41.78px !important;
      background-color: #ffffff !important;
    }
  `
  t = t || identity
  const wrapperRef = React.useRef()
  const [search, finalSearch, setSearch] = useOnStopTypingState('')
  let [{ localOptions, loading, canLoadMore }, fetchOptions, fetchMoreOptions] = useLazyOptions(options, {
    disablePagination
  })

  idField = idField || 'id'
  labelField = labelField || 'name'
  descriptionField = descriptionField || 'description'

  const { handleClose, setOpen, open, current, setCurrent, handleChange } = useControlledDropdown({
    wrapperRef,
    value,
    options: localOptions,
    multiple,
    onChange,
    idField,
    onClose
  })

  const [editor] = React.useState(() =>
    compose(
      InterpolationPlugin.extend({ fullControl: false }), //
      withReact
    )(createEditor())
  )

  editor.singleLine = true
  editor.singleItem = true

  const isValueObject = is(Object, value)

  if (!selectedLabel && is(Array, options) && !isValueObject) {
    selectedLabel = compose(prop(labelField), find(propEq(idField, value)))(options)
  }

  if (!selectedLabel) selectedLabel = value

  if (!!selectedLabel?.length && selectedLabel?.map) {
    selectedLabel = selectedLabel.map(compose(t, prop(labelField))).join(', ')
  } else if (is(Object, selectedLabel)) {
    selectedLabel = t(prop(labelField, selectedLabel))
  } else selectedLabel = t(selectedLabel)

  React.useEffect(() => {
    if (open) {
      fetchOptions(finalSearch)
      setCurrent(0)
    }
  }, [finalSearch, open])

  const AVAILABLE_TYPES = {
    user: ['raw_user', 'raw_metadata'],
    place: ['raw_place'],
    checklist: ['raw_checklist'],
    asset: ['raw_asset']
  }

  const filteredInterpolations = interpolations.filter(interpolation =>
    AVAILABLE_TYPES[origin].includes(interpolation?.type)
  )

  const initialValue = deserializeText(isInterpolation(value?.id) ? value?.id : '', filteredInterpolations)

  const handleSetValue = value => {
    const serializedValue = serializeText(value)
    const cleanValue = isInterpolation(serializedValue)
      ? serializedValue.match(INTERPOLATION_REGEX)?.[0]
      : serializedValue
    setSearch(cleanValue)
    if (!!assign) {
      onChange({ __typename: assign.entityType, id: cleanValue })
    } else {
      onChange({ id: cleanValue })
    }
    editor.children = deserializeText(cleanValue, filteredInterpolations)
  }

  function handlePicker(value) {
    handleChange(value)
    setSearch(value?.name)
  }

  const hasInterpolation = isInterpolation(value?.id) || value?.id === null

  return (
    <div css={styles} className="select" ref={wrapperRef}>
      <Relative zIndex="auto">
        <Panel padding={0} radius={0.4}>
          <Slate editor={editor} value={initialValue} onChange={handleSetValue} {...props}>
            <EditorContext size="small">
              <Relative zIndex="auto">
                <Separator className="inputWrapper" paddingV={0.6} paddingH={0.7}>
                  <Editable
                    onKeyDown={useEditorKeydown(editor)}
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                  />
                  <Absolute right={0} top={-1.5} zIndex="auto">
                    <UniqueInterpolationPlugin.Dropdown options={filteredInterpolations} />
                  </Absolute>
                </Separator>
              </Relative>
            </EditorContext>
          </Slate>
        </Panel>
        {!hasInterpolation && (
          <Absolute left={0} top={0} zIndex="auto" width="100%">
            <div className="inputButtonWrapper">
              <Input
                placeholder={placeholder}
                value={!open ? selectedLabel : search}
                onChange={setSearch}
                onFocus={() => setOpen(true)}
                iconRight={!open ? 'BsCaretDown' : 'BsCaretUp'}
                disabled={disabled}
                onKeyPress={e => {
                  e.key === 'Enter' && e.preventDefault()
                }}
                onKeyDown={onKeyDown}
                autoComplete="new-password"
                {...props}
              />
            </div>
            <Relative zIndex="auto">
              {open && (
                <Absolute top={0} left={0} right={0} zIndex={10}>
                  <Panel
                    padding="0"
                    radius={0.3}
                    borderColor="light3"
                    shadow="dark4"
                    maxHeight={16}
                    scroll
                    onScrollToBottom={() => fetchMoreOptions(finalSearch)}
                  >
                    {!loading && !localOptions?.length && (
                      <Separator weight={2}>
                        <Text color="dark5">Nenhum item encontrado</Text>
                      </Separator>
                    )}
                    <Picker
                      t={t}
                      currentIndex={current}
                      useEmptyOption={!disableEmptyOption && !finalSearch}
                      options={localOptions}
                      onChange={handlePicker}
                      idField={idField}
                      labelField={labelField}
                      descriptionField={descriptionField}
                      value={value}
                      multiple={multiple}
                      selectOnlyId={selectOnlyId}
                      header={
                        !finalSearch
                          ? header
                          : !!onCreate && (
                              <LabelIconListItem
                                label={`Criar novo: ${finalSearch}`}
                                icon="RiCheckboxCircleLine"
                                iconColor="white"
                                color="white"
                                bgColor="purple"
                                onClick={() => {
                                  onCreate(search)
                                  handleClose()
                                }}
                              />
                            )
                      }
                      disableItem={disableItem}
                      reasonForDisablePresenter={reasonForDisablePresenter}
                    />
                    {canLoadMore && <Loading size="small" />}
                  </Panel>
                </Absolute>
              )}
            </Relative>
          </Absolute>
        )}
      </Relative>
    </div>
  )
}

export default SelectWithInterpolation
